/**
 * 物料分类管理 Store
 * 支持用户自定义分类和子分类
 */

export type SubCategory = {
  id: string;
  name: string;
  icon?: string;
  sortOrder: number;
};

export type MaterialCategory = {
  id: string;
  name: string;
  icon: string;
  color: string;
  sortOrder: number;
  subCategories: SubCategory[];
  createdAt: string;
  updatedAt: string;
};

// 默认分类配置
export const DEFAULT_CATEGORIES: MaterialCategory[] = [
  {
    id: 'cat_album',
    name: '图册',
    icon: 'ri-image-2-line',
    color: '#8b5cf6',
    sortOrder: 0,
    subCategories: [
      { id: 'sub_brand', name: '品牌画册', icon: 'ri-book-open-line', sortOrder: 0 },
      { id: 'sub_product', name: '产品手册', icon: 'ri-box-3-line', sortOrder: 1 },
      { id: 'sub_case', name: '案例集', icon: 'ri-award-line', sortOrder: 2 },
    ],
    createdAt: '2026-01-01',
    updatedAt: '2026-01-01',
  },
  {
    id: 'cat_card',
    name: '名片',
    icon: 'ri-id-card-line',
    color: '#64748b',
    sortOrder: 1,
    subCategories: [
      { id: 'sub_sales_card', name: '销售名片', icon: 'ri-user-star-line', sortOrder: 0 },
      { id: 'sub_cs_card', name: '客服名片', icon: 'ri-customer-service-line', sortOrder: 1 },
      { id: 'sub_tech_card', name: '技术名片', icon: 'ri-code-box-line', sortOrder: 2 },
    ],
    createdAt: '2026-01-01',
    updatedAt: '2026-01-01',
  },
  {
    id: 'cat_video',
    name: '视频',
    icon: 'ri-video-line',
    color: '#f43f5e',
    sortOrder: 2,
    subCategories: [
      { id: 'sub_demo', name: '产品演示', icon: 'ri-slideshow-line', sortOrder: 0 },
      { id: 'sub_case_video', name: '案例访谈', icon: 'ri-mic-line', sortOrder: 1 },
      { id: 'sub_tutorial', name: '教程视频', icon: 'ri-graduation-cap-line', sortOrder: 2 },
    ],
    createdAt: '2026-01-01',
    updatedAt: '2026-01-01',
  },
  {
    id: 'cat_sales',
    name: '销售物料',
    icon: 'ri-folder-chart-line',
    color: '#f97316',
    sortOrder: 3,
    subCategories: [
      { id: 'sub_toolkit', name: '销售工具包', icon: 'ri-tools-line', sortOrder: 0 },
      { id: 'sub_script', name: '话术模板', icon: 'ri-chat-quote-line', sortOrder: 1 },
      { id: 'sub_pricing', name: '定价方案', icon: 'ri-price-tag-3-line', sortOrder: 2 },
      { id: 'sub_comparison', name: '竞品对比', icon: 'ri-bar-chart-grouped-line', sortOrder: 3 },
    ],
    createdAt: '2026-01-01',
    updatedAt: '2026-01-01',
  },
];

const STORAGE_KEY = 'material_categories_v1';

class CategoryStore {
  private categories: MaterialCategory[] = [];
  private listeners: Set<(cats: MaterialCategory[]) => void> = new Set();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.categories = JSON.parse(stored);
      } else {
        this.categories = [...DEFAULT_CATEGORIES];
        this.saveToStorage();
      }
    } catch {
      this.categories = [...DEFAULT_CATEGORIES];
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.categories));
    } catch {
      // ignore
    }
  }

  private notify() {
    this.listeners.forEach(fn => fn([...this.categories]));
  }

  getAll(): MaterialCategory[] {
    return [...this.categories].sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getById(id: string): MaterialCategory | undefined {
    return this.categories.find(c => c.id === id);
  }

  getSubCategory(categoryId: string, subId: string): SubCategory | undefined {
    const cat = this.getById(categoryId);
    return cat?.subCategories.find(s => s.id === subId);
  }

  // 添加主分类
  addCategory(name: string, icon: string, color: string): MaterialCategory {
    const newCat: MaterialCategory = {
      id: `cat_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      icon,
      color,
      sortOrder: this.categories.length,
      subCategories: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.categories.push(newCat);
    this.saveToStorage();
    this.notify();
    return newCat;
  }

  // 更新主分类
  updateCategory(id: string, updates: Partial<Omit<MaterialCategory, 'id' | 'subCategories'>>): boolean {
    const idx = this.categories.findIndex(c => c.id === id);
    if (idx === -1) return false;
    
    this.categories[idx] = {
      ...this.categories[idx],
      ...updates,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    this.notify();
    return true;
  }

  // 删除主分类
  deleteCategory(id: string): boolean {
    const idx = this.categories.findIndex(c => c.id === id);
    if (idx === -1) return false;
    
    this.categories.splice(idx, 1);
    // 重新排序
    this.categories.forEach((c, i) => { c.sortOrder = i; });
    this.saveToStorage();
    this.notify();
    return true;
  }

  // 添加子分类
  addSubCategory(categoryId: string, name: string, icon?: string): SubCategory | null {
    const cat = this.categories.find(c => c.id === categoryId);
    if (!cat) return null;

    const newSub: SubCategory = {
      id: `sub_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      icon,
      sortOrder: cat.subCategories.length,
    };
    cat.subCategories.push(newSub);
    cat.updatedAt = new Date().toISOString();
    this.saveToStorage();
    this.notify();
    return newSub;
  }

  // 更新子分类
  updateSubCategory(categoryId: string, subId: string, updates: Partial<Omit<SubCategory, 'id'>>): boolean {
    const cat = this.categories.find(c => c.id === categoryId);
    if (!cat) return false;
    
    const subIdx = cat.subCategories.findIndex(s => s.id === subId);
    if (subIdx === -1) return false;

    cat.subCategories[subIdx] = { ...cat.subCategories[subIdx], ...updates };
    cat.updatedAt = new Date().toISOString();
    this.saveToStorage();
    this.notify();
    return true;
  }

  // 删除子分类
  deleteSubCategory(categoryId: string, subId: string): boolean {
    const cat = this.categories.find(c => c.id === categoryId);
    if (!cat) return false;
    
    const subIdx = cat.subCategories.findIndex(s => s.id === subId);
    if (subIdx === -1) return false;

    cat.subCategories.splice(subIdx, 1);
    // 重新排序
    cat.subCategories.forEach((s, i) => { s.sortOrder = i; });
    cat.updatedAt = new Date().toISOString();
    this.saveToStorage();
    this.notify();
    return true;
  }

  // 排序分类
  reorderCategories(orderedIds: string[]) {
    const newOrder: MaterialCategory[] = [];
    orderedIds.forEach((id, idx) => {
      const cat = this.categories.find(c => c.id === id);
      if (cat) {
        cat.sortOrder = idx;
        newOrder.push(cat);
      }
    });
    // 添加未在orderedIds中的分类
    this.categories.forEach(c => {
      if (!orderedIds.includes(c.id)) {
        newOrder.push(c);
      }
    });
    this.categories = newOrder.map((c, i) => ({ ...c, sortOrder: i }));
    this.saveToStorage();
    this.notify();
  }

  // 排序子分类
  reorderSubCategories(categoryId: string, orderedIds: string[]) {
    const cat = this.categories.find(c => c.id === categoryId);
    if (!cat) return;

    const newOrder: SubCategory[] = [];
    orderedIds.forEach((id, idx) => {
      const sub = cat.subCategories.find(s => s.id === id);
      if (sub) {
        sub.sortOrder = idx;
        newOrder.push(sub);
      }
    });
    cat.subCategories.forEach(s => {
      if (!orderedIds.includes(s.id)) {
        newOrder.push(s);
      }
    });
    cat.subCategories = newOrder.map((s, i) => ({ ...s, sortOrder: i }));
    cat.updatedAt = new Date().toISOString();
    this.saveToStorage();
    this.notify();
  }

  // 重置为默认
  resetToDefault() {
    this.categories = JSON.parse(JSON.stringify(DEFAULT_CATEGORIES));
    this.saveToStorage();
    this.notify();
  }

  subscribe(listener: (cats: MaterialCategory[]) => void): () => void {
    this.listeners.add(listener);
    listener([...this.categories]);
    return () => this.listeners.delete(listener);
  }
}

export const categoryStore = new CategoryStore();

// 预设图标列表
export const PRESET_ICONS = [
  { key: 'ri-image-2-line', label: '图片' },
  { key: 'ri-id-card-line', label: '名片' },
  { key: 'ri-video-line', label: '视频' },
  { key: 'ri-folder-chart-line', label: '图表' },
  { key: 'ri-file-list-line', label: '文档' },
  { key: 'ri-book-open-line', label: '书籍' },
  { key: 'ri-award-line', label: '奖项' },
  { key: 'ri-box-3-line', label: '产品' },
  { key: 'ri-tools-line', label: '工具' },
  { key: 'ri-chat-quote-line', label: '对话' },
  { key: 'ri-price-tag-3-line', label: '标签' },
  { key: 'ri-bar-chart-grouped-line', label: '统计' },
  { key: 'ri-user-star-line', label: '用户' },
  { key: 'ri-customer-service-line', label: '客服' },
  { key: 'ri-code-box-line', label: '代码' },
  { key: 'ri-slideshow-line', label: '演示' },
  { key: 'ri-mic-line', label: '录音' },
  { key: 'ri-graduation-cap-line', label: '教育' },
  { key: 'ri-briefcase-line', label: '公文包' },
  { key: 'ri-calendar-event-line', label: '日历' },
  { key: 'ri-mail-line', label: '邮件' },
  { key: 'ri-phone-line', label: '电话' },
  { key: 'ri-map-pin-line', label: '位置' },
  { key: 'ri-building-line', label: '建筑' },
];

// 预设颜色
export const PRESET_COLORS = [
  { key: '#f97316', label: '橙色' },
  { key: '#8b5cf6', label: '紫色' },
  { key: '#f43f5e', label: '玫瑰红' },
  { key: '#10b981', label: '翠绿' },
  { key: '#3b82f6', label: '蓝色' },
  { key: '#06b6d4', label: '青色' },
  { key: '#eab308', label: '黄色' },
  { key: '#ec4899', label: '粉色' },
  { key: '#64748b', label: '灰色' },
  { key: '#14b8a6', label: ' teal' },
  { key: '#6366f1', label: '靛蓝' },
  { key: '#84cc16', label: '青柠' },
];